import { Injectable, RendererFactory2, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CookieConsentService } from '../../core/privacy/cookie-consent.service';
import { analyticsPath } from './analytics-url';
import { isPrivateTicketPage } from '../../core/privacy/recovery-link';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class GtagLoaderService {
  private initialized = false;
  private readonly consent = inject(CookieConsentService);
  private readonly rendererFactory = inject(RendererFactory2);
  private readonly document = inject(DOCUMENT);
  private readonly renderer = this.rendererFactory.createRenderer(null, null);

  init(): void {
    if (!this.consent.allows('analytics')) return;
    if (
      this.document.defaultView &&
      isPrivateTicketPage(this.document.defaultView.location.pathname)
    )
      return;
    if (this.initialized) {
      this.setDisabled(false);
      return;
    }
    const win = typeof window !== 'undefined' ? (window as any) : null;
    if (!win || !this.document) return;
    if (win.__gtagLoaded) return;

    const ga = environment.ga;
    if (!ga?.enabled || !ga.id) return;

    this.setDisabled(false);
    this.ensureDataLayer(win);
    this.ensureGtag(win);
    this.appendScript(ga.id);
    win.gtag('consent', 'default', {
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    win.gtag('set', 'ads_data_redaction', true);
    win.gtag('js', new Date());
    win.gtag('config', ga.id, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      page_location: win.location.origin + analyticsPath(win.location.pathname),
      page_referrer: '',
    });

    win.__gtagLoaded = true;
    this.initialized = true;
  }

  disable(): void {
    this.setDisabled(true);
  }

  private setDisabled(disabled: boolean): void {
    const win = this.document.defaultView as
      | (Window & Record<string, unknown>)
      | null;
    if (win && environment.ga.id)
      win[`ga-disable-${environment.ga.id}`] = disabled;
  }

  private ensureDataLayer(win: any): void {
    win.dataLayer = win.dataLayer || [];
  }

  private ensureGtag(win: any): void {
    if (typeof win.gtag === 'function') return;
    win.gtag = (...args: unknown[]) => {
      win.dataLayer.push(args);
    };
  }

  private appendScript(id: string): void {
    const head =
      this.document.head || this.document.getElementsByTagName('head')[0];
    if (!head) return;
    const existing = this.document.querySelector(`script[data-ga-id="${id}"]`);
    if (existing) return;

    const script = this.renderer.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    this.renderer.setAttribute(script, 'data-ga-id', id);
    this.renderer.appendChild(head, script);
  }
}
